import { Injectable } from '@angular/core';

import { AUTH_STORAGE_KEYS } from '../constants/auth-storage.constants';
import { AuthSession } from '../models/auth-session.model';

@Injectable({
  providedIn: 'root'
})
export class AuthSessionStorageService {
  private static readonly LEGACY_ACCESS_TOKEN_KEYS = ['access_token', 'token'] as const;

  loadSession(): AuthSession | null {
    const fromLocalStorage = this.readFromStorage(localStorage);
    if (fromLocalStorage) {
      return fromLocalStorage;
    }

    return this.readFromStorage(sessionStorage);
  }

  saveSession(session: AuthSession): void {
    localStorage.removeItem(AUTH_STORAGE_KEYS.session);
    sessionStorage.removeItem(AUTH_STORAGE_KEYS.session);

    const targetStorage = session.rememberMe ? localStorage : sessionStorage;
    targetStorage.setItem(AUTH_STORAGE_KEYS.session, JSON.stringify(session));
    localStorage.setItem(AUTH_STORAGE_KEYS.rememberMe, String(session.rememberMe));
  }

  loadAccessToken(): string | null {
    const sessionToken = this.loadSession()?.accessToken?.trim();

    if (sessionToken) {
      return sessionToken;
    }

    for (const storage of [localStorage, sessionStorage]) {
      for (const tokenKey of AuthSessionStorageService.LEGACY_ACCESS_TOKEN_KEYS) {
        const rawToken = storage.getItem(tokenKey)?.trim();

        if (rawToken) {
          return rawToken;
        }
      }
    }

    return null;
  }

  clearSession(): void {
    localStorage.removeItem(AUTH_STORAGE_KEYS.session);
    localStorage.removeItem(AUTH_STORAGE_KEYS.rememberMe);
    sessionStorage.removeItem(AUTH_STORAGE_KEYS.session);
  }

  private readFromStorage(storage: Storage): AuthSession | null {
    const serializedSession = storage.getItem(AUTH_STORAGE_KEYS.session);

    if (!serializedSession) {
      return null;
    }

    try {
      return JSON.parse(serializedSession) as AuthSession;
    } catch {
      storage.removeItem(AUTH_STORAGE_KEYS.session);
      return null;
    }
  }
}
